import type { StructuredContentDocument } from '@repo/contracts/multi-use/structured-content/types';
import { css, cx } from '@repo/pandacss/styled-system/css';
import { AnalyticsType } from '@repo/segment/types';
import type { CtaType } from '@repo/supergraph/marketplace/types';
import type { AuthorQuote } from '@repo/supergraph/roundup/base/authorQuote/types';
import { MdExpandMore } from 'react-icons/md';
import { Details } from '../../../Details';
import { BottomLine } from './BottomLine';
import { ProsAndCons } from './ProsAndCons';
import { isValidProductType } from './utils';

type Props = {
  bottomLine?: StructuredContentDocument;
  cta?: CtaType;
  ctaAnalytics: AnalyticsType;
  pros?: StructuredContentDocument[];
  cons?: StructuredContentDocument[];
  isDefaultExpanded?: boolean;
  productType?: string;
  authorQuote?: AuthorQuote;
  displayName?: string;
};

export const OurTake = ({
  bottomLine,
  cta,
  ctaAnalytics,
  isDefaultExpanded = false,
  productType,
  pros,
  cons,
  authorQuote,
  displayName,
}: Props) => {
  if (!isValidProductType(productType) || !bottomLine) {
    return null;
  }

  const panelContentId = `panel-content-our-take-${ctaAnalytics.offerId}-${ctaAnalytics.index}`;

  return (
    <Details
      variant="simple"
      detailsProps={{
        open: isDefaultExpanded,
        'data-testid': 'accordion-wrapper',
        className: css({
          borderTopWidth: 'thin',
          borderTopStyle: 'solid',
          borderTopColor: 'neutral.lighter',
        }),
      }}
      summaryComponentProps={{
        'data-testid': 'accordion-summary',
        'aria-controls': panelContentId,
        className: css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingInlineStart: '4',
          paddingInlineEnd: '4',
          paddingBlockStart: '3',
          paddingBlockEnd: '3',
          _hover: {
            outlineWidth: { base: 'none', sm: 'medium' },
            outlineStyle: { base: 'none', sm: 'solid' },
            outlineColor: { base: 'transparent', sm: 'blue.light' },
            backgroundColor: 'blue.lightest',
          },
        }),
      }}
      SummaryComponent={
        <>
          <span
            className={css({
              textStyle: 'textSmall',
              paddingLeft: { md: '4' },
            })}
          >
            Our Take
          </span>
          <MdExpandMore className={cx('expand-icon', css({ opacity: '[0.54]', fontSize: 'xl' }))} />
        </>
      }
    >
      <div
        data-testid="accordion-details"
        id={panelContentId}
        className={css({
          paddingTop: '4',
          paddingRight: '4',
          paddingBottom: '4',
          paddingLeft: '4',
        })}
      >
        <ProsAndCons pros={pros} cons={cons} />
        <BottomLine
          bottomLine={bottomLine}
          cta={cta}
          ctaAnalytics={ctaAnalytics}
          authorQuote={authorQuote}
          displayName={displayName}
        />
      </div>
    </Details>
  );
};
